<script setup lang="ts">
/**
 * API 金鑰的權限範圍勾選格（批147）——建立與編輯共用。
 *
 * 依後端目錄畫：每類一列、讀／寫兩格；只有讀的類只畫一格（寫那一格留「—」佔位，
 * 讓兩欄上下對齊）。連動規則（勾寫自動勾讀、取消讀一併取消寫）全在
 * `lib/apiKeyScopes.ts`，這裡只負責把點擊轉成 `update:modelValue`。
 *
 * 勾選格下方唯讀列出「網頁專屬（金鑰一律不能做）」——讓建立的人知道有些事
 * 不管勾什麼都做不到（刪除客戶、停站、員工帳號寫入…），免得以為是勾漏了。
 *
 * ★ 切換不換字重、計數放在工具列尾端（伸縮區）：勾選時三顆按鈕的位置不動。
 */
import { computed } from 'vue'

import UiButton from '@/components/UiButton.vue'
import type { ApiKeyScopeCatalog, ApiKeyScopeCategory } from '@/api/ls'
import type { ScopeAction } from '@/lib/apiKeyScopes'
import {
  categoryState,
  clearScopes,
  hasAction,
  normalizeScopes,
  scopeOf,
  selectAll,
  selectAllRead,
  setScope,
} from '@/lib/apiKeyScopes'

const props = defineProps<{ catalog: ApiKeyScopeCatalog; modelValue: string[] }>()
const emit = defineEmits<{ 'update:modelValue': [value: string[]] }>()

const ACTIONS: { action: ScopeAction; label: string }[] = [
  { action: 'read', label: '讀取' },
  { action: 'write', label: '寫入' },
]

/** 實際會送出的項數（含寫隱含的讀）。 */
const count = computed(() => normalizeScopes(props.modelValue, props.catalog).length)
const total = computed(() => props.catalog.categories.reduce((n, c) => n + c.actions.length, 0))

function endpointsOf(c: ApiKeyScopeCategory, action: ScopeAction): number {
  return c.actions.find((a) => a.action === action)?.endpoints ?? 0
}
function isOn(c: ApiKeyScopeCategory, action: ScopeAction): boolean {
  return categoryState(props.modelValue, c)[action]
}
function toggle(c: ApiKeyScopeCategory, action: ScopeAction, on: boolean) {
  emit('update:modelValue', setScope(props.modelValue, scopeOf(c.key, action), on, props.catalog))
}
</script>

<template>
  <div class="sp">
    <div class="sp-tools">
      <UiButton variant="secondary" size="sm" type="button" @click="emit('update:modelValue', selectAllRead(modelValue, catalog))">
        全選讀取
      </UiButton>
      <UiButton variant="secondary" size="sm" type="button" @click="emit('update:modelValue', selectAll(catalog))">
        全選
      </UiButton>
      <UiButton variant="secondary" size="sm" type="button" @click="emit('update:modelValue', clearScopes())">
        清除
      </UiButton>
      <span class="sp-count">已選 {{ count }} / {{ total }} 項</span>
    </div>

    <div class="sp-grid" role="group" aria-label="權限範圍">
      <template v-for="c in catalog.categories" :key="c.key">
        <div class="sp-cat">{{ c.label }}</div>
        <template v-for="a in ACTIONS" :key="a.action">
          <label
            v-if="hasAction(c, a.action)"
            class="sp-cell"
            :class="{ on: isOn(c, a.action), write: a.action === 'write' }"
            :title="`${c.label}：${a.label}（涵蓋 ${endpointsOf(c, a.action)} 支端點）`"
          >
            <input
              type="checkbox"
              :checked="isOn(c, a.action)"
              :data-scope="scopeOf(c.key, a.action)"
              @change="toggle(c, a.action, ($event.target as HTMLInputElement).checked)"
            />
            <span class="sp-act">{{ a.label }}</span>
            <span class="sp-n">{{ endpointsOf(c, a.action) }} 支</span>
          </label>
          <span v-else class="sp-cell none" aria-hidden="true">—</span>
        </template>
      </template>
    </div>
    <p class="subtle sp-hint">勾「寫入」會自動勾「讀取」；取消「讀取」會一併取消「寫入」。「N 支」＝這個動作涵蓋的端點數。</p>

    <div v-if="catalog.web_only.length" class="sp-web">
      <div class="sp-web-title">網頁專屬（金鑰一律不能做）</div>
      <ul class="sp-web-list">
        <li v-for="w in catalog.web_only" :key="`${w.method} ${w.path}`" :title="`${w.method} ${w.path}`">
          <span class="sp-web-label">{{ w.label }}</span>
          <code class="mono sp-web-path">{{ w.method }} {{ w.path }}</code>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.sp-tools {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: var(--sp-2);
}
/* 計數放在尾端伸縮區：數字位數變化不會推動前面三顆按鈕。 */
.sp-count {
  margin-left: auto;
  font-size: var(--fs-12);
  color: var(--fg-3);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.sp-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 4px 6px;
  align-items: center;
  border: 1px solid var(--border);
  border-radius: var(--r-md);
  padding: 6px 8px;
  background: var(--bg-panel);
}
.sp-cat {
  font-size: var(--fs-13);
  color: var(--fg);
  min-width: 0;
  overflow-wrap: anywhere;
  padding: 4px 0;
}
/* 一格＝一個 label（整格可點）。選中只換底色與框色，**不換字重**（切換不跳動）。 */
.sp-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 104px;
  height: 30px;
  padding: 0 10px;
  border: 1px solid var(--border-strong);
  border-radius: var(--r-sm);
  background: var(--bg);
  color: var(--fg-2);
  font-size: var(--fs-12);
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.sp-cell:hover {
  border-color: var(--brand-400);
}
.sp-cell input {
  margin: 0;
}
.sp-cell.on {
  background: var(--brand-50);
  border-color: var(--brand-400);
  color: var(--brand-700);
}
.sp-cell.write.on {
  background: var(--st-pending-bg);
  border-color: var(--warning);
  color: var(--st-pending-fg);
}
[data-theme='dark'] .sp-cell.on {
  background: var(--bg-active);
  color: var(--brand-400);
}
[data-theme='dark'] .sp-cell.write.on {
  background: var(--st-pending-bg);
  color: var(--st-pending-fg);
}
.sp-cell.none {
  justify-content: center;
  border-style: dashed;
  border-color: var(--border);
  background: transparent;
  color: var(--fg-4);
  cursor: default;
}
.sp-n {
  margin-left: auto;
  color: var(--fg-4);
  font-size: var(--fs-11);
  font-variant-numeric: tabular-nums;
}
.sp-cell.on .sp-n {
  color: inherit;
  opacity: 0.75;
}
.sp-hint {
  margin: var(--sp-2) 0 0;
  line-height: var(--lh-base);
}
.sp-web {
  margin-top: var(--sp-3);
  padding-top: var(--sp-3);
  border-top: 1px solid var(--border-2);
}
.sp-web-title {
  font-size: var(--fs-12);
  font-weight: 600;
  color: var(--fg-2);
  margin-bottom: 6px;
}
.sp-web-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
  gap: 4px var(--sp-3);
}
.sp-web-list li {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 3px 0;
}
.sp-web-label {
  font-size: var(--fs-12);
  color: var(--fg-2);
}
/* 路徑一行到底、過長以省略號收尾（完整內容在 li 的 title）：讓它折行的話
   `POST` 與路徑會被拆到兩行，十六項排起來又長又亂（截圖驗收時抓到的）。 */
code.mono.sp-web-path {
  display: block;
  max-width: 100%;
  font-size: var(--fs-11);
  color: var(--fg-3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
